import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { CreditCard, Search, ChevronLeft, ChevronRight, Download, Check, X, Wallet, Eye, Copy, CheckCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select } from '../../../components/ui/select';
import { useToast } from '../../../hooks/useToast';
import { Link } from 'react-router-dom';
import { useCheckPermissions } from '../../../middleware/checkPermissions';

interface Transaction {
  id: string;
  amount: number;
  created_at: string;
  status: string;
  payment_method: string;
  phone_number: string;
  votes_count: number;
  contestant: {
    name: string;
    unique_code: string;
  };
  contest: {
    name: string;
    slug: string;
  };
}

interface Withdrawal {
  id: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  payment_method: string;
  phone_number?: string;
  bank_details?: any;
  created_at: string;
  users: {
    name: string;
    email: string;
  };
}

interface WithdrawalStats {
  user_id: string;
  user_name: string;
  user_email: string;
  total_amount: number;
}

export const TransactionList = () => {
  useCheckPermissions(['admin']);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const itemsPerPageOptions = [50, 100, 200, 500];

  const [activeTab, setActiveTab] = useState<'transactions' | 'withdrawals'>('transactions');
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [isLoadingWithdrawals, setIsLoadingWithdrawals] = useState(true);
  const [withdrawalStats, setWithdrawalStats] = useState<WithdrawalStats[]>([]);
  const { toast } = useToast();

  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'completed', label: 'Complété' },
    { value: 'pending', label: 'En attente' },
    { value: 'failed', label: 'Échoué' }
  ];

  const paymentMethodOptions = [
    { value: 'all', label: 'Toutes les méthodes' },
    { value: 'mobile_money', label: 'Mobile Money' },
    { value: 'card', label: 'Paypal / CB' }
  ];

  const dateOptions = [
    { value: 'all', label: 'Toutes les dates' },
    { value: 'today', label: "Aujourd'hui" },
    { value: 'week', label: '7 derniers jours' },
    { value: 'month', label: '30 derniers jours' },
    { value: 'year', label: '12 derniers mois' }
  ];

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount).replace('XAF', 'FCFA');
  };

  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions();
    } else {
      fetchWithdrawals();
      fetchWithdrawalStats();
    }
    // Toujours revenir à la page 1 si les filtres changent
    setCurrentPage(1);
  }, [activeTab, statusFilter, paymentMethodFilter, dateFilter, searchTerm]);

  // Recherche sur toutes les colonnes visibles
  const searchLower = searchTerm.trim().toLowerCase();
  const filteredTransactions = searchLower
    ? transactions.filter(tx => {
        // Champs à rechercher : Date, Concours, Candidat, Votant, Méthode, Statut, Votes, Montant
        const dateStr = new Date(tx.created_at).toLocaleString('fr-FR');
        const concours = tx.contest?.name?.toLowerCase() || '';
        const candidat = tx.contestant?.name?.toLowerCase() || '';
        const votant = tx.phone_number?.toLowerCase() || '';
        const methode = tx.payment_method?.toLowerCase() || '';
        const statut = tx.status?.toLowerCase() || '';
        const votes = String(tx.votes_count || '').toLowerCase();
        const montant = String(tx.amount || '').toLowerCase();
        return (
          dateStr.toLowerCase().includes(searchLower) ||
          concours.includes(searchLower) ||
          candidat.includes(searchLower) ||
          votant.includes(searchLower) ||
          methode.includes(searchLower) ||
          statut.includes(searchLower) ||
          votes.includes(searchLower) ||
          montant.includes(searchLower)
        );
      })
    : transactions;
  const totalFiltered = filteredTransactions.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  // Si la page courante dépasse le total, revenir à la dernière page
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);

      let query = supabase
        .from('transactions')
        .select(`
          id,
          amount,
          created_at,
          status,
          payment_method,
          phone_number,
          votes_count,
          contestants!inner (
            id,
            name,
            unique_code,
            contest_id,
            contests!inner (
              id,
              name,
              slug,
              status,
              end_date
            )
          )
        `, { count: 'exact' });

      // Appliquer les filtres
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (paymentMethodFilter !== 'all') {
        query = query.eq('payment_method', paymentMethodFilter);
      }

      if (dateFilter !== 'all') {
        const today = new Date();
        let startDate = new Date();

        switch (dateFilter) {
          case 'today':
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            startDate.setDate(today.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(today.getMonth() - 1);
            break;
          case 'year':
            startDate.setFullYear(today.getFullYear() - 1);
            break;
        }

        query = query.gte('created_at', startDate.toISOString());
      }

      const { data, count, error } = await query.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Transformer les transactions pour l'affichage
      const now = new Date();
      const filteredData = data?.filter(tx => {
        const contest = tx.contestants?.contests;
        // Exclure si le concours a le statut 'finished'
        if (contest?.status && contest.status.toLowerCase() === 'finished') return false;
        // Exclure si la date de fin du concours est passée
        if (contest?.end_date && new Date(contest.end_date) < now) return false;
        return true;
      });

      const processedTransactions = filteredData?.map(tx => ({
        id: tx.id,
        amount: Number(tx.amount),
        created_at: tx.created_at,
        status: tx.status,
        payment_method: tx.payment_method,
        phone_number: tx.phone_number,
        votes_count: tx.votes_count,
        contestant: {
          name: tx.contestants?.name,
          unique_code: tx.contestants?.unique_code
        },
        contest: {
          name: tx.contestants?.contests?.name,
          slug: tx.contestants?.contests?.slug
        }
      })) || [];

      setTransactions(processedTransactions);

      setIsLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des transactions:', error);
      setIsLoading(false);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      setIsLoadingWithdrawals(true);
      const { data, error } = await supabase
        .from('withdrawals')
        .select(`
          *,
          users (
            name,
            email
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWithdrawals(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des retraits:', error);
    } finally {
      setIsLoadingWithdrawals(false);
    }
  };

  const fetchWithdrawalStats = async () => {
    try {
      const { data, error } = await supabase
        .from('withdrawals')
        .select(`
          user_id,
          amount,
          users (
            name,
            email
          )
        `)
        .eq('status', 'approved');

      if (error) throw error;

      // Calculer le total par utilisateur
      const statsMap = new Map<string, WithdrawalStats>();
      data?.forEach(withdrawal => {
        const existing = statsMap.get(withdrawal.user_id);
        if (existing) {
          existing.total_amount += Number(withdrawal.amount);
        } else {
          statsMap.set(withdrawal.user_id, {
            user_id: withdrawal.user_id,
            user_name: withdrawal.users.name,
            user_email: withdrawal.users.email,
            total_amount: Number(withdrawal.amount)
          });
        }
      });

      setWithdrawalStats(Array.from(statsMap.values()));
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques de retrait:', error);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const formatPaymentMethod = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'mobile_money':
        return 'Mobile Money';
      case 'card':
        return 'Paypal / CB';
      default:
        return method;
    }
  };

  const formatStatus = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Complété';
      case 'pending':
        return 'En attente';
      case 'failed':
        return 'Échoué';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-400/10 text-green-400';
      case 'pending':
        return 'bg-yellow-400/10 text-yellow-400';
      case 'failed':
        return 'bg-red-400/10 text-red-400';
      default:
        return 'bg-gray-400/10 text-gray-400';
    }
  };

  const handleExport = async () => {
    try {
      // Récupérer toutes les transactions avec les filtres actuels
      let query = supabase
        .from('transactions')
        .select(`
          id,
          amount,
          created_at,
          status,
          payment_method,
          phone_number,
          votes_count,
          contestants!inner (
            id,
            name,
            unique_code,
            contest_id,
            contests!inner (
              id,
              name,
              slug
            )
          )
        `);

      // Appliquer les mêmes filtres que pour l'affichage
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (paymentMethodFilter !== 'all') {
        query = query.eq('payment_method', paymentMethodFilter);
      }

      if (dateFilter !== 'all') {
        const today = new Date();
        let startDate = new Date();

        switch (dateFilter) {
          case 'today':
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            startDate.setDate(today.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(today.getMonth() - 1);
            break;
          case 'year':
            startDate.setFullYear(today.getFullYear() - 1);
            break;
        }

        query = query.gte('created_at', startDate.toISOString());
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('Aucune transaction à exporter');
      }

      // Transformer les données pour l'export
      const exportData = data.map(tx => ({
        date: new Date(tx.created_at).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }),
        concours: tx.contestants?.contests?.name || '',
        candidat: tx.contestants?.name || '',
        votant: tx.phone_number || '',
        methode: formatPaymentMethod(tx.payment_method),
        statut: formatStatus(tx.status),
        votes: tx.votes_count,
        montant: formatPrice(Number(tx.amount))
      }));

      // Créer le contenu CSV
      const headers = ['Date', 'Concours', 'Candidat', 'Votant', 'Méthode', 'Statut', 'Votes', 'Montant'];
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => Object.values(row).map(value => `"${value}"`).join(','))
      ].join('\n');

      // Créer et télécharger le fichier
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const date = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');
      link.href = window.URL.createObjectURL(blob);
      link.download = `transactions_${date}.csv`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error: any) {
      console.error('Erreur lors de l\'export:', error);
      // Afficher un message d'erreur à l'utilisateur
      // toast({
      //   variant: 'destructive',
      //   title: 'Erreur',
      //   description: error.message || 'Impossible d\'exporter les transactions'
      // });
    }
  };

  const handleUpdateWithdrawalStatus = async (id: string, status: 'approved' | 'rejected') => {
    try {
      const { error } = await supabase
        .from('withdrawals')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      fetchWithdrawals();
      toast({
        title: 'Statut mis à jour',
        description: `La demande de retrait a été ${status === 'approved' ? 'approuvée' : 'rejetée'}.`,
        variant: 'success'
      });
    } catch (error) {
      console.error('Error updating withdrawal status:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la mise à jour du statut.',
        variant: 'error'
      });
    }
  };

  const getWithdrawalStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-400/10 text-green-400';
      case 'rejected':
        return 'bg-red-400/10 text-red-400';
      default:
        return 'bg-yellow-400/10 text-yellow-400';
    }
  };

  const getWithdrawalStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Approuvé';
      case 'rejected':
        return 'Rejeté';
      default:
        return 'En attente';
    }
  };

  // Bouton pour révéler l'ID de la transaction
  const RevealIdButton = ({ id }: { id: string }) => {
    const [show, setShow] = useState(false);
    const [copied, setCopied] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      } catch {}
    };

    const handleValidate = async () => {
      setLoading(true);
      setError(null);
      setSuccess(false);
      try {
        // Validation universelle via Supabase (côté client)
        const { error } = await supabase
          .from('transactions')
          .update({ status: 'completed', updated_at: new Date().toISOString() })
          .eq('id', id);
        if (error) throw error;
        
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
        
        // Rafraîchir la liste des transactions
        fetchTransactions();
      } catch (e: any) {
        console.error('Erreur lors de la validation:', e);
        setError("Erreur lors de la validation");
      } finally {
        setLoading(false);
        setConfirm(false);
      }
    };

    const handleReset = async () => {
      setLoading(true);
      setError(null);
      setResetSuccess(false);
      try {
        // 1. D'abord, récupérer les informations de la transaction
        const { data: transaction, error: fetchError } = await supabase
          .from('transactions')
          .select('*')
          .eq('id', id)
          .single();
        
        if (fetchError) throw fetchError;
        if (!transaction) throw new Error('Transaction non trouvée');
        
        // Vérifier si la transaction est au statut 'completed'
        if (transaction.status !== 'completed') {
          throw new Error('Seules les transactions complétées peuvent être annulées');
        }
        
        // 2. Supprimer les votes correspondants dans la table votes
        const { error: deleteVotesError } = await supabase
          .from('votes')
          .delete()
          .eq('transaction_id', id);
        
        if (deleteVotesError) {
          console.error('Erreur lors de la suppression des votes:', deleteVotesError);
          throw deleteVotesError;
        }
        
        // 3. Mettre à jour le statut de la transaction à 'pending'
        const { error: updateError } = await supabase
          .from('transactions')
          .update({ 
            status: 'pending', 
            updated_at: new Date().toISOString() 
          })
          .eq('id', id);
          
        if (updateError) throw updateError;
        
        // 4. Si la transaction a un contestant_id et des votes, décréditer les votes
        if (transaction.contestant_id && transaction.votes_count > 0) {
          // D'abord récupérer le nombre actuel de votes du candidat
          const { data: contestant, error: contestantFetchError } = await supabase
            .from('contestants')
            .select('votes_count')
            .eq('id', transaction.contestant_id)
            .single();
            
          if (contestantFetchError) throw contestantFetchError;
          if (!contestant) throw new Error('Candidat non trouvé');
          
          // Calculer le nouveau nombre de votes (ne pas descendre en dessous de 0)
          const newVotesCount = Math.max(0, contestant.votes_count - transaction.votes_count);
          
          // Mettre à jour le nombre de votes du candidat
          const { error: contestantUpdateError } = await supabase
            .from('contestants')
            .update({ votes_count: newVotesCount })
            .eq('id', transaction.contestant_id);
            
          if (contestantUpdateError) throw contestantUpdateError;
        }
        
        // Mettre à jour l'interface
        setResetSuccess(true);
        setTimeout(() => setResetSuccess(false), 2000);
        
        // Rafraîchir la liste des transactions
        fetchTransactions();
      } catch (e: any) {
        console.error('Erreur lors de l\'annulation:', e);
        setError(e.message || "Erreur lors de l'annulation");
      } finally {
        setLoading(false);
        setConfirmReset(false);
      }
    };

    return show ? (
      <span className="flex items-center gap-2">
        <span className="font-mono text-xs text-yellow-400 select-all bg-black/30 px-1 py-0.5 rounded">{id}</span>
        <button
          type="button"
          className={`p-1 rounded hover:bg-yellow-500/70 ${copied ? 'bg-yellow-500/70' : 'bg-white/10'} text-yellow-400 hover:text-white transition-all`}
          onClick={handleCopy}
          title="Copier l'ID"
        >
          <Copy className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="p-1 rounded bg-white/10 text-yellow-400 hover:bg-yellow-500 hover:text-white transition-all"
          onClick={() => setShow(false)}
          title="Masquer l'ID"
        >
          <Eye className="w-4 h-4" />
        </button>
        {/* Validation manuelle */}
        <button
          type="button"
          className="p-1 rounded bg-white/10 text-green-500 hover:bg-green-600 hover:text-white transition-all"
          onClick={() => setConfirm(true)}
          disabled={loading || success}
          title="Valider manuellement la transaction"
        >
          <CheckCircle className="w-4 h-4" />
        </button>
        {/* Bouton d'annulation */}
        <button
          type="button"
          className="p-1 rounded bg-white/10 text-red-500 hover:bg-red-600 hover:text-white transition-all"
          onClick={() => setConfirmReset(true)}
          disabled={loading || resetSuccess}
          title="Annuler la transaction (remettre en pending)"
        >
          <X className="w-4 h-4" />
        </button>
        {copied && <span className="text-xs text-green-400 ml-1">Copié !</span>}
        {success && <span className="text-xs text-green-400 ml-1">Validé !</span>}
        {resetSuccess && <span className="text-xs text-yellow-400 ml-1">Annulée !</span>}
        {error && <span className="text-xs text-red-400 ml-1">{error}</span>}
        {/* Confirmation modal simple pour validation */}
        {confirm && (
          <span className="absolute bg-black/80 border border-yellow-500 rounded p-2 z-50 flex flex-col items-center gap-2 left-1/2 -translate-x-1/2">
            <span className="text-sm text-yellow-200 mb-1">Valider cette transaction ?</span>
            <div className="flex gap-2">
              <button onClick={handleValidate} disabled={loading} className="px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700">Oui</button>
              <button onClick={() => setConfirm(false)} className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700">Non</button>
            </div>
          </span>
        )}
        {/* Confirmation modal pour annulation */}
        {confirmReset && (
          <span className="absolute bg-black/80 border border-red-500 rounded p-2 z-50 flex flex-col items-center gap-2 left-1/2 -translate-x-1/2">
            <span className="text-sm text-red-200 mb-1">Annuler cette transaction ? Les votes seront décredités.</span>
            <div className="flex gap-2">
              <button onClick={handleReset} disabled={loading} className="px-2 py-1 rounded bg-yellow-600 text-white hover:bg-yellow-700">Oui</button>
              <button onClick={() => setConfirmReset(false)} className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700">Non</button>
            </div>
          </span>
        )}
      </span>
    ) : (
      <button
        type="button"
        className="bg-white/10 hover:bg-yellow-500 hover:text-white text-yellow-400 rounded p-1 transition-all flex items-center justify-center"
        onClick={() => setShow(true)}
        title="Afficher l'ID de la transaction"
      >
        <Eye className="w-4 h-4" />
      </button>
    );
  };

  return (
    <AdminLayout>
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* En-tête de la page */}
          <div className="mb-8">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur opacity-75"></div>
                <CreditCard className="h-8 w-8 text-yellow-400 relative" />
              </div>
              <h1 className="text-3xl font-bold">
                <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                  Transactions
                </span>
              </h1>
            </div>
            <p className="mt-2 text-gray-400">
              Gérez et suivez toutes les transactions de la plateforme
            </p>
          </div>

          {/* Onglets */}
          <div className="mb-6 flex space-x-4 border-b border-white/10">
            <button
              onClick={() => setActiveTab('transactions')}
              className={`pb-4 text-sm font-medium transition-colors relative ${
                activeTab === 'transactions'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Transactions
            </button>
            <button
              onClick={() => setActiveTab('withdrawals')}
              className={`pb-4 text-sm font-medium transition-colors relative ${
                activeTab === 'withdrawals'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Demandes de retrait
            </button>
          </div>

          {activeTab === 'transactions' ? (
            <>
              {/* Filtres et recherche pour les transactions */}
              <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="bg-white/5 border-white/10 text-white"
                  icon={<Search className="h-4 w-4 text-gray-400" />}
                />
                <Select
                  value={statusFilter}
                  onValueChange={setStatusFilter}
                  options={statusOptions}
                />
                <Select
                  value={paymentMethodFilter}
                  onValueChange={setPaymentMethodFilter}
                  options={paymentMethodOptions}
                />
                <Select
                  value={dateFilter}
                  onValueChange={setDateFilter}
                  options={dateOptions}
                />
                <Button
                  variant="outline"
                  className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
                  onClick={handleExport}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exporter
                </Button>
              </div>

              {/* Tableau des transactions */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-4 text-sm font-medium text-gray-400">ID</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Date</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Concours</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Candidat</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Votant</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Méthode</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Statut</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Votes</th>
                        <th className="text-right p-4 text-sm font-medium text-gray-400">Montant</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {paginatedTransactions.map((tx, index) => (
                        <tr key={tx.id} className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                          <td className="p-4 text-sm">
                            <RevealIdButton id={tx.id} />
                          </td>
                          <td className="p-4 text-sm text-gray-300">
                            {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td className="p-4 text-sm">
                            <Link 
                              to={`/c/${tx.contest.slug}`} 
                              className="font-medium text-white hover:text-yellow-400 transition-colors"
                            >
                              {tx.contest.name}
                            </Link>
                          </td>
                          <td className="p-4 text-sm text-white">
                            <Link 
                              to={`/${tx.contestant.unique_code}`} 
                              className="text-white hover:text-yellow-400 transition-colors"
                            >
                              {tx.contestant.name}
                            </Link>
                          </td>
                          <td className="p-4 text-sm text-white">{tx.phone_number || 'Non renseigné'}</td>
                          <td className="p-4 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              tx.payment_method === 'mobile_money' 
                                ? 'bg-yellow-400/10 text-yellow-400' 
                                : 'bg-blue-400/10 text-blue-400'
                            }`}>
                              {formatPaymentMethod(tx.payment_method)}
                            </span>
                          </td>
                          <td className="p-4 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(tx.status)}`}>
                              {formatStatus(tx.status)}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-white">{tx.votes_count}</td>
                          <td className="p-4 text-sm text-right">
                            <span className="font-medium text-white">{formatPrice(tx.amount)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="border-t border-white/10 px-4 py-3 flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <Button
                      onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                      className="border-white/10 text-white hover:bg-white/5"
                    >
                      Précédent
                    </Button>
                    <Button
                      onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      size="sm"
                      className="border-white/10 text-white hover:bg-white/5"
                    >
                      Suivant
                    </Button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-center gap-4">
                      <p className="text-sm text-gray-400">
                        Page <span className="font-medium text-white">{currentPage}</span> sur{' '}
                        <span className="font-medium text-white">{totalPages}</span>                        
                      </p>
                      <label className="ml-4 text-sm text-gray-400 flex items-center gap-1">
                        Par page :
                        <select
                          value={itemsPerPage}
                          onChange={e => setItemsPerPage(Number(e.target.value))}
                          className="bg-transparent border border-white/10 rounded px-2 py-1 text-white focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        >
                          {itemsPerPageOptions.map(opt => (
                            <option key={opt} value={opt} className="text-black">{opt}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <Button
                          onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                          disabled={currentPage === 1}
                          variant="outline"
                          size="sm"
                          className="border-white/10 text-white hover:bg-white/5 rounded-l-md"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                          <Button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            variant={pageNum === currentPage ? 'default' : 'outline'}
                            size="sm"
                            className={`border-white/10 text-white hover:bg-white/5 ${pageNum === currentPage ? 'bg-yellow-500 text-white' : ''}`}
                            disabled={pageNum === currentPage}
                          >
                            {pageNum}
                          </Button>
                        ))}
                        <Button
                          onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                          disabled={currentPage === totalPages}
                          variant="outline"
                          size="sm"
                          className="border-white/10 text-white hover:bg-white/5 rounded-r-md ml-2"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              
              {/* Tableau des retraits */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Date</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Client</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Montant</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Méthode</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Détails</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Statut</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {withdrawals.map((withdrawal, index) => (
                        <tr key={withdrawal.id} className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                          <td className="p-4 text-sm text-gray-300">
                            {new Date(withdrawal.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td className="p-4 text-sm text-gray-300">
                            <div>
                              <div className="font-medium text-white">{withdrawal.users.name}</div>
                              <div className="text-gray-400">{withdrawal.users.email}</div>
                            </div>
                          </td>
                          <td className="p-4 text-sm font-medium text-white">
                            {formatPrice(withdrawal.amount)}
                          </td>
                          <td className="p-4 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              withdrawal.payment_method === 'mobile_money'
                                ? 'bg-yellow-400/10 text-yellow-400'
                                : 'bg-blue-400/10 text-blue-400'
                            }`}>
                              {withdrawal.payment_method === 'mobile_money' ? 'Mobile Money' : 'Virement bancaire'}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-gray-300">
                            {withdrawal.payment_method === 'mobile_money'
                              ? withdrawal.phone_number
                              : withdrawal.bank_details?.details}
                          </td>
                          <td className="p-4 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getWithdrawalStatusColor(withdrawal.status)}`}>
                              {getWithdrawalStatusText(withdrawal.status)}
                            </span>
                          </td>
                          <td className="p-4 text-sm">
                            {withdrawal.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleUpdateWithdrawalStatus(withdrawal.id, 'approved')}
                                  className="bg-green-500 hover:bg-green-600"
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleUpdateWithdrawalStatus(withdrawal.id, 'rejected')}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!isLoadingWithdrawals && (!withdrawals || withdrawals.length === 0) && (
                        <tr>
                          <td colSpan={7} className="p-4 text-center text-gray-400">
                            Aucune demande de retrait trouvée
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default TransactionList;
